import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socket, { sessionId } from '../socket';
import { resetGame, setRematchMine } from '../store/slices/lobbySlice';
import { resetTTT } from '../store/slices/ticTacToeSlice';
import GameOverDialog from './GameOverDialog';

export default function TicTacToe() {
  const dispatch = useDispatch();
  const { board, currentTurn, winner, marks } = useSelector((s) => s.ticTacToe);
  const room = useSelector((s) => s.lobby.currentRoom);
  const opponentLeft = useSelector((s) => s.lobby.opponentLeft);
  const opponentDisconnected = useSelector((s) => s.lobby.opponentDisconnected);
  const rematchMine = useSelector((s) => s.lobby.rematchMine);
  const rematchOpponent = useSelector((s) => s.lobby.rematchOpponent);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null); // move sent, awaiting server
  const pendingTimerRef = useRef(null);
  // Game state is keyed by sessionId on the server (socket.id changes on
  // every reconnect and would never match)
  const myId = sessionId;

  const myMark = marks[myId];
  const isMyTurn = currentTurn === myId;

  // Any server update (move result, restore, rematch reset) clears the marker
  useEffect(() => {
    setPendingIndex(null);
    clearTimeout(pendingTimerRef.current);
  }, [board]);

  const handleClick = (index) => {
    if (!isMyTurn || winner || board[index] || !socket.connected) return;
    if (pendingIndex !== null) return; // one move in flight at a time

    // Show the mark immediately (faded) — confirmed by the next ttt:update
    setPendingIndex(index);
    clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => setPendingIndex(null), 4000);

    socket.emit('ttt:move', { roomId: room.id, index });
  };

  const leave = () => {
    socket.emit('game:leave', { roomId: room.id });
    dispatch(resetTTT());
    dispatch(resetGame());
  };

  const requestRematch = () => {
    if (!socket.connected) return;
    socket.emit('rematch:request', { roomId: room.id });
    dispatch(setRematchMine(true));
  };

  const handleLeaveClick = () => {
    if (winner || opponentLeft) {
      leave();
    } else {
      setShowLeaveConfirm(true);
    }
  };

  const getPlayerName = (id) => {
    if (!room) return '';
    const idx = room.players.indexOf(id);
    return room.playerNames?.[idx] || id;
  };

  const opponentName = getPlayerName(room?.players?.find((p) => p !== myId)) || 'Your opponent';

  let statusText;
  if (opponentLeft) {
    statusText = 'Opponent left the game!';
  } else if (winner === 'draw') {
    statusText = "It's a draw!";
  } else if (winner) {
    statusText = winner === myId ? '🎉 You win!' : `${getPlayerName(winner)} wins!`;
  } else if (isMyTurn) {
    statusText = `Your turn (${myMark})`;
  } else {
    statusText = `Waiting for ${getPlayerName(currentTurn)}...`;
  }

  return (
    <div className="game-container">
      {showLeaveConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🚪 Leave Game</h3>
            <p>Are you sure? The game is still in progress. <strong>Leaving will count as a forfeit</strong> and your opponent will be notified.</p>
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={() => { setShowLeaveConfirm(false); leave(); }}>Yes, Leave</button>
              <button className="btn btn-primary" onClick={() => setShowLeaveConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {opponentLeft && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🚪 Opponent Left</h3>
            <p><strong>{opponentName}</strong> has left the game.</p>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={leave}>Back to Lobby</button>
            </div>
          </div>
        </div>
      )}

      {!opponentLeft && opponentDisconnected && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>⚠️ Opponent Disconnected</h3>
            <p><strong>{opponentName}</strong> lost connection. Waiting for them to reconnect...</p>
            <div className="spinner" style={{ margin: '10px auto' }} />
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={leave}>Leave Game</button>
            </div>
          </div>
        </div>
      )}

      {winner && !opponentLeft && !opponentDisconnected && !showLeaveConfirm && (
        <GameOverDialog
          result={winner === 'draw' ? 'draw' : winner === myId ? 'win' : 'lose'}
          rematchMine={rematchMine}
          rematchOpponent={rematchOpponent}
          onRestart={requestRematch}
          onLeave={leave}
        />
      )}

      <div className="game-top-bar">
        <h2>❌⭕ Tic-Tac-Toe</h2>
        <button className="btn btn-small btn-leave" onClick={handleLeaveClick}>
          {winner || opponentLeft ? 'Back to Lobby' : 'Leave Game'}
        </button>
      </div>
      <p className="game-vs">
        {room?.playerNames?.[0]} (X) vs {room?.playerNames?.[1]} (O)
      </p>
      <p className={`status ${winner === myId ? 'status-win' : ''}`}>{statusText}</p>

      <div className="ttt-board">
        {board.map((cell, i) => {
          const isPending = pendingIndex === i && !cell;
          const shown = cell || (isPending ? myMark : '');
          return (
            <button
              key={i}
              className={`ttt-cell ${shown === 'X' ? 'mark-x' : shown === 'O' ? 'mark-o' : ''} ${
                isMyTurn && !cell && !winner && pendingIndex === null ? 'clickable' : ''
              } ${isPending ? 'mark-pending' : ''}`}
              onClick={() => handleClick(i)}
              disabled={!isMyTurn || !!winner || !!cell || pendingIndex !== null}
            >
              {shown}
            </button>
          );
        })}
      </div>
    </div>
  );
}
